// apps/web/src/components/miner/WeightHero.tsx
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Sparkline } from '@gonka-wallet/ui-tokens';
import { ChevronUpIcon, ChevronDownIcon } from './icons.js';
import { formatWeight } from './minerFormat.js';
import type { MinerStats } from '../../lib/minerApi.js';

interface Props {
  readonly stats: MinerStats | null;
  readonly weightSeries: readonly number[]; // oldest -> newest
}

const MIN_POINTS_FOR_STATS = 3;

export function WeightHero({ stats, weightSeries }: Props): ReactNode {
  const { t } = useTranslation();

  if (!stats) {
    return (
      <div
        data-state="skeleton"
        className="mb-3.5 rounded-2xl bg-surface-2 px-4 py-4 animate-pulse"
        aria-busy="true"
      >
        <div className="h-9 w-32 rounded bg-surface-3" />
      </div>
    );
  }

  const inactive = stats.is_active === false;

  const last = weightSeries.length > 0 ? weightSeries[weightSeries.length - 1] : null;
  const prev = weightSeries.length > 1 ? weightSeries[weightSeries.length - 2] : null;
  const delta = last != null && prev != null ? last - prev : null;
  const showDelta = !inactive && delta != null && delta !== 0;

  const avg = weightSeries.length > 0
    ? Math.round(weightSeries.reduce((s, v) => s + v, 0) / weightSeries.length)
    : null;
  const minVal = weightSeries.length > 0 ? Math.min(...weightSeries) : null;
  const maxVal = weightSeries.length > 0 ? Math.max(...weightSeries) : null;

  const hasStats = weightSeries.length >= MIN_POINTS_FOR_STATS && avg != null && minVal != null && maxVal != null;

  return (
    <div className="mb-3.5 rounded-2xl bg-surface-2 px-4 pt-4 pb-3.5">
      <div className="text-[10px] font-semibold uppercase tracking-[1.2px] text-text-tertiary">
        {t('miner.weight')}
      </div>

      {/* Top row: big value + delta on the left, min/avg/max stack on the right */}
      <div className="mt-0.5 flex items-start justify-between gap-4">
        <div className="flex items-baseline gap-2.5">
          <div className={`font-mono text-[36px] font-semibold leading-none tracking-[-0.025em] ${inactive ? 'text-text-tertiary' : 'text-text-primary'}`}>
            {formatWeight(stats.weight)}
          </div>
          {showDelta && <DeltaPill delta={delta!} />}
        </div>

        {hasStats && (
          <div className="flex flex-col items-end gap-0.5 font-mono text-[11px] text-text-tertiary leading-snug">
            <StatLine label={t('miner.weightChartMin')} value={formatWeight(minVal!)} />
            <StatLine label={t('miner.weightChartAvg')} value={formatWeight(avg!)} />
            <StatLine label={t('miner.weightChartMax')} value={formatWeight(maxVal!)} />
          </div>
        )}
      </div>

      <div className="mt-2 flex flex-wrap items-center gap-x-3.5 gap-y-1 text-xs text-text-secondary">
        <span>
          <strong className="font-mono font-medium text-text-primary">
            {stats.rank != null ? `#${stats.rank}` : '—'}
          </strong>
          {' '}{t('miner.weightOf', { total: stats.total_participants })}
        </span>
        <span className="h-[3px] w-[3px] rounded-full bg-text-disabled" aria-hidden />
        <span>
          {t('miner.weightShare')}{' '}
          <strong className="font-mono font-medium text-text-primary">{stats.share.toFixed(2)}%</strong>
        </span>
      </div>

      {/* Minimalist trend line — 30 ep, no markers, no fill */}
      {hasStats && (
        <div className="mt-3 opacity-80">
          <Sparkline
            values={weightSeries}
            color="var(--color-text-primary)"
            width={340}
            height={24}
            strokeWidth={1}
            fill={false}
          />
        </div>
      )}

      {!hasStats && !inactive && weightSeries.length > 0 && (
        <div className="mt-3 rounded-lg bg-surface-3 px-3 py-2 text-[10px] font-semibold uppercase tracking-[1.2px] text-text-tertiary">
          {t('miner.weightCollecting', { count: weightSeries.length })}
        </div>
      )}

      {inactive && (
        <div className="mt-3 text-[11px] text-text-tertiary">
          {t('miner.weightInactiveBanner')}
        </div>
      )}
    </div>
  );
}

function StatLine({ label, value }: { label: string; value: string }): ReactNode {
  return (
    <div className="inline-flex items-baseline gap-1.5">
      <span className="uppercase tracking-[0.5px] text-text-tertiary">{label}</span>
      <span className="font-medium text-text-primary tabular-nums">{value}</span>
    </div>
  );
}

function DeltaPill({ delta }: { delta: number }): ReactNode {
  const positive = delta > 0;
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 font-mono text-xs font-semibold ${positive ? 'bg-pos-tint text-pos' : 'bg-neg-tint text-neg'}`}
    >
      {positive ? (
        <ChevronUpIcon width={10} height={10} strokeWidth={1.6} />
      ) : (
        <ChevronDownIcon width={10} height={10} strokeWidth={1.6} />
      )}
      {positive ? `+${delta}` : `${delta}`}
    </span>
  );
}
